import React, { useEffect, useMemo, useState } from 'react'
import { Palette, X } from 'lucide-react'
import type { CellFormat, GridController } from '../../compositor/GridController'
import type { SpreadsheetConditionalFormatRule, SpreadsheetRangeAddress } from '../../protocol/spreadsheetEngineProtocol'
import {
  CONDITIONAL_FORMAT_BORDER_STYLE_OPTIONS,
  CONDITIONAL_FORMAT_DATA_BAR_DIRECTION_OPTIONS,
  CONDITIONAL_FORMAT_ICON_SET_OPTIONS,
  CONDITIONAL_FORMAT_ICON_THRESHOLD_TYPE_OPTIONS,
  CONDITIONAL_FORMAT_OPERATOR_OPTIONS_BY_KIND,
  CONDITIONAL_FORMAT_PRESET_STYLES,
  CONDITIONAL_FORMAT_RULE_FILTER_OPTIONS,
  CONDITIONAL_FORMAT_RULE_TYPES,
  CONDITIONAL_FORMAT_VISUAL_THRESHOLD_TYPES,
  buildConditionalFormatEditDraft,
  buildConditionalFormatDialogApplyPlan,
  buildConditionalFormatDialogClearPlan,
  buildConditionalFormatDialogReorderPlan,
  buildConditionalFormatValueLabels,
  buildCustomConditionalFormatStyle,
  conditionalFormatIsVisualRule,
  conditionalFormatStyleColor,
  filterConditionalFormatRules,
  formatConditionalFormatRuleRange,
  sortConditionalFormatRules,
  type ConditionalFormatKind,
  type ConditionalFormatRuleFilter,
  type ConditionalFormatDialogApplyPlan,
  type ConditionalFormatDialogClearPlan,
  type ConditionalFormatDialogReorderPlan,
} from '../../spreadsheet/conditionalFormattingPresentation'
import './ExcelWorkbookChrome.css'

type ExcelConditionalFormattingDialogProps = {
  controller: GridController
  selectedRange: SpreadsheetRangeAddress
  selectionLabel: string
  rules?: SpreadsheetConditionalFormatRule[]
  loading?: boolean
  error?: string | null
  onClose: () => void
  onApplied?: () => void
  onRefresh?: () => void | Promise<void>
  onDeleteRule?: (ruleId: string) => void | Promise<void>
  onReorderRules?: (ruleIds: string[]) => void | Promise<void>
}


export function ExcelConditionalFormattingDialog({
  controller,
  selectedRange,
  selectionLabel,
  rules = [],
  loading = false,
  error = null,
  onClose,
  onApplied,
  onRefresh,
  onDeleteRule,
  onReorderRules,
}: ExcelConditionalFormattingDialogProps) {
  const [kind, setKind] = useState<ConditionalFormatKind>('cellIs')
  const [operator, setOperator] = useState('greaterThan')
  const [formula1, setFormula1] = useState('')
  const [formula2, setFormula2] = useState('')
  const [styleId, setStyleId] = useState(CONDITIONAL_FORMAT_PRESET_STYLES[0].id)
  const [customFillColor, setCustomFillColor] = useState(() => conditionalFormatStyleColor(CONDITIONAL_FORMAT_PRESET_STYLES[0].style, 'fill', '#ffc7ce'))
  const [customTextColor, setCustomTextColor] = useState(() => conditionalFormatStyleColor(CONDITIONAL_FORMAT_PRESET_STYLES[0].style, 'font', '#9c0006'))
  const [customBold, setCustomBold] = useState(false)
  const [customItalic, setCustomItalic] = useState(false)
  const [customUnderline, setCustomUnderline] = useState(false)
  const [customBorderStyle, setCustomBorderStyle] = useState('none')
  const [customBorderColor, setCustomBorderColor] = useState('#1f2937')
  const [stopIfTrue, setStopIfTrue] = useState(false)
  const [rangeDraft, setRangeDraft] = useState(() => formatConditionalFormatRuleRange(selectedRange))
  const [dataBarColor, setDataBarColor] = useState('#63c384')
  const [dataBarNegativeColor, setDataBarNegativeColor] = useState('#f87171')
  const [dataBarBorderColor, setDataBarBorderColor] = useState('#6aa84f')
  const [dataBarDirection, setDataBarDirection] = useState('leftToRight')
  const [dataBarShowValue, setDataBarShowValue] = useState(true)
  const [scaleMinColor, setScaleMinColor] = useState('#f8696b')
  const [scaleMidColor, setScaleMidColor] = useState('#ffeb84')
  const [scaleMaxColor, setScaleMaxColor] = useState('#63be7b')
  const [iconSet, setIconSet] = useState('3Arrows')
  const [iconSetShowValue, setIconSetShowValue] = useState(true)
  const [iconSetReverse, setIconSetReverse] = useState(false)
  const [iconSetThresholdType, setIconSetThresholdType] = useState('percent')
  const [iconSetThresholds, setIconSetThresholds] = useState('')
  const [visualMinType, setVisualMinType] = useState('lowestValue')
  const [visualMaxType, setVisualMaxType] = useState('highestValue')
  const [visualMidType, setVisualMidType] = useState('percentile')
  const [visualMidValue, setVisualMidValue] = useState('50')
  const [draftError, setDraftError] = useState<string | null>(null)
  const [ruleFilter, setRuleFilter] = useState<ConditionalFormatRuleFilter>('selection')
  const [editingRule, setEditingRule] = useState<SpreadsheetConditionalFormatRule | null>(null)
  const selectedStyle = useMemo(() => (
    CONDITIONAL_FORMAT_PRESET_STYLES.find((style) => style.id === styleId) ?? CONDITIONAL_FORMAT_PRESET_STYLES[0]
  ), [styleId])
  const selectedCustomStyle = useMemo(() => buildCustomConditionalFormatStyle({
    fillColor: customFillColor,
    textColor: customTextColor,
    bold: customBold,
    italic: customItalic,
    underline: customUnderline,
    borderStyle: customBorderStyle,
    borderColor: customBorderColor,
  }), [customFillColor, customTextColor, customBold, customItalic, customUnderline, customBorderStyle, customBorderColor])
  const operatorOptions = CONDITIONAL_FORMAT_OPERATOR_OPTIONS_BY_KIND[kind] ?? CONDITIONAL_FORMAT_OPERATOR_OPTIONS_BY_KIND.cellIs
  const visualRule = conditionalFormatIsVisualRule(kind)
  const { firstValueLabel, secondValueLabel } = buildConditionalFormatValueLabels(kind)
  const sortedRules = useMemo(() => sortConditionalFormatRules(rules), [rules])
  const visibleRules = useMemo(() => (
    filterConditionalFormatRules({ rules: sortedRules, filter: ruleFilter, selectedRange })
  ), [ruleFilter, selectedRange, sortedRules])

  const syncCustomStyleDraft = (style: CellFormat) => {
    const font = style.font && typeof style.font === 'object' && !Array.isArray(style.font)
      ? style.font as Record<string, unknown>
      : {}
    const border = style.border && typeof style.border === 'object' && !Array.isArray(style.border)
      ? style.border as Record<string, unknown>
      : {}
    const borderSide = ['top', 'right', 'bottom', 'left']
      .map((key) => border[key])
      .find((side) => side && typeof side === 'object' && !Array.isArray(side)) as Record<string, unknown> | undefined
    setCustomFillColor(conditionalFormatStyleColor(style, 'fill', '#ffc7ce'))
    setCustomTextColor(conditionalFormatStyleColor(style, 'font', '#9c0006'))
    setCustomBold(style.bold === true || font.bold === true)
    setCustomItalic(style.italic === true || font.italic === true)
    setCustomUnderline(Boolean(style.underline ?? font.underline))
    setCustomBorderStyle(String(borderSide?.style ?? 'none'))
    setCustomBorderColor(conditionalFormatStyleColor({ textColor: borderSide?.color as string | undefined }, 'font', '#1f2937'))
  }

  const selectPresetStyle = (nextStyleId: string) => {
    const preset = CONDITIONAL_FORMAT_PRESET_STYLES.find((style) => style.id === nextStyleId) ?? CONDITIONAL_FORMAT_PRESET_STYLES[0]
    setStyleId(preset.id)
    syncCustomStyleDraft(preset.style)
  }

  useEffect(() => {
    if (!editingRule) setRangeDraft(formatConditionalFormatRuleRange(selectedRange))
  }, [editingRule, selectedRange])

  const beginEditRule = (rule: SpreadsheetConditionalFormatRule) => {
    setEditingRule(rule)
    const draft = buildConditionalFormatEditDraft({ rule, fallbackStyle: selectedStyle.style })
    setKind(draft.kind)
    setOperator(draft.operator)
    setFormula1(draft.formula1)
    setFormula2(draft.formula2)
    setStyleId(draft.styleId)
    setCustomFillColor(draft.customFillColor)
    setCustomTextColor(draft.customTextColor)
    setCustomBold(draft.customBold)
    setCustomItalic(draft.customItalic)
    setCustomUnderline(draft.customUnderline)
    setCustomBorderStyle(draft.customBorderStyle)
    setCustomBorderColor(draft.customBorderColor)
    setStopIfTrue(draft.stopIfTrue)
    setRangeDraft(draft.rangeDraft)
    setDataBarColor(draft.dataBarColor)
    setDataBarNegativeColor(draft.dataBarNegativeColor)
    setDataBarBorderColor(draft.dataBarBorderColor)
    setDataBarDirection(draft.dataBarDirection)
    setDataBarShowValue(draft.dataBarShowValue)
    setScaleMinColor(draft.scaleMinColor)
    setScaleMidColor(draft.scaleMidColor)
    setScaleMaxColor(draft.scaleMaxColor)
    setIconSet(draft.iconSet)
    setIconSetShowValue(draft.iconSetShowValue)
    setIconSetReverse(draft.iconSetReverse)
    setIconSetThresholdType(draft.iconSetThresholdType)
    setIconSetThresholds(draft.iconSetThresholds)
    setVisualMinType(draft.visualMinType)
    setVisualMaxType(draft.visualMaxType)
    setVisualMidType(draft.visualMidType)
    setVisualMidValue(draft.visualMidValue)
    setDraftError(null)
  }

  const resetDraft = () => {
    setEditingRule(null)
    setKind('cellIs')
    setOperator('greaterThan')
    setFormula1('')
    setFormula2('')
    setStyleId(CONDITIONAL_FORMAT_PRESET_STYLES[0].id)
    syncCustomStyleDraft(CONDITIONAL_FORMAT_PRESET_STYLES[0].style)
    setStopIfTrue(false)
    setRangeDraft(formatConditionalFormatRuleRange(selectedRange))
    setDataBarColor('#63c384')
    setDataBarNegativeColor('#f87171')
    setDataBarBorderColor('#6aa84f')
    setDataBarDirection('leftToRight')
    setDataBarShowValue(true)
    setScaleMinColor('#f8696b')
    setScaleMidColor('#ffeb84')
    setScaleMaxColor('#63be7b')
    setIconSet('3Arrows')
    setIconSetShowValue(true)
    setIconSetReverse(false)
    setIconSetThresholdType('percent')
    setIconSetThresholds('')
    setVisualMinType('lowestValue')
    setVisualMaxType('highestValue')
    setVisualMidType('percentile')
    setVisualMidValue('50')
    setDraftError(null)
  }

  const executeApplyPlan = (plan: ConditionalFormatDialogApplyPlan): boolean => {
    if (plan.action === 'blocked') {
      setDraftError(plan.error)
      return false
    }
    controller.upsertConditionalFormatRule(plan.rule)
    return true
  }

  const executeClearPlan = (plan: ConditionalFormatDialogClearPlan) => {
    if (plan.action === 'clear-selection-rules') {
      controller.clearConditionalFormatsFromSelection()
    }
  }

  const executeReorderPlan = (plan: ConditionalFormatDialogReorderPlan) => {
    if (plan.action !== 'reorder-rules') return
    void Promise.resolve(onReorderRules?.(plan.ruleIds)).then(() => {
      onApplied?.()
      void onRefresh?.()
    })
  }

  const applyRule = () => {
    const applied = executeApplyPlan(buildConditionalFormatDialogApplyPlan({
      editingRule,
      selectedRange,
      sortedRules,
      customStyle: selectedCustomStyle,
      kind,
      operator,
      formula1,
      formula2,
      styleId,
      customFillColor,
      customTextColor,
      customBold,
      customItalic,
      customUnderline,
      customBorderStyle,
      customBorderColor,
      stopIfTrue,
      rangeDraft,
      dataBarColor,
      dataBarNegativeColor,
      dataBarBorderColor,
      dataBarDirection,
      dataBarShowValue,
      scaleMinColor,
      scaleMidColor,
      scaleMaxColor,
      iconSet,
      iconSetShowValue,
      iconSetReverse,
      iconSetThresholdType,
      iconSetThresholds,
      visualMinType,
      visualMaxType,
      visualMidType,
      visualMidValue,
    }))
    if (!applied) return
    onApplied?.()
    resetDraft()
    void onRefresh?.()
  }

  const clearSelectionRules = () => {
    executeClearPlan(buildConditionalFormatDialogClearPlan())
    onApplied?.()
    onClose()
  }

  const moveRule = (ruleId: string, direction: -1 | 1) => {
    executeReorderPlan(buildConditionalFormatDialogReorderPlan({
      ruleId,
      direction,
      visibleRules,
      sortedRules,
    }))
  }

  return (
    <div className="fg-excel-format-dialog-backdrop" role="presentation" onMouseDown={(event) => {
      if (event.target === event.currentTarget) onClose()
    }}>
      <div className="fg-excel-conditional-format-dialog" role="dialog" aria-modal="true" aria-label="Conditional Formatting">
        <div className="fg-excel-format-dialog-header">
          <strong className="fg-excel-cell-metadata-title">
            <Palette size={16} />
            Conditional Formatting
          </strong>
          <button type="button" className="fg-excel-icon-button" aria-label="Close" title="Close" onClick={onClose}>
            <X size={16} />
          </button>
        </div>
        <div className="fg-excel-cell-metadata-range">Applies to {selectionLabel}</div>
        <div className="fg-excel-format-grid">
          <label>
            <span>Rule type</span>
            <select value={kind} aria-label="Rule type" title="Rule type" onChange={(event) => {
              const nextKind = event.target.value as ConditionalFormatKind
              setKind(nextKind)
              setOperator(CONDITIONAL_FORMAT_OPERATOR_OPTIONS_BY_KIND[nextKind]?.[0]?.value ?? 'greaterThan')
            }}>
              {CONDITIONAL_FORMAT_RULE_TYPES.map((entry) => <option key={entry.id} value={entry.id}>{entry.label}</option>)}
            </select>
          </label>
          <label>
            <span>Format with</span>
            <select value={styleId} aria-label="Format with" title="Format with" onChange={(event) => selectPresetStyle(event.target.value)}>
              {CONDITIONAL_FORMAT_PRESET_STYLES.map((entry) => <option key={entry.id} value={entry.id}>{entry.label}</option>)}
            </select>
          </label>
          <label>
            <span>Operator</span>
            <select value={operator} aria-label="Operator" title="Operator" onChange={(event) => setOperator(event.target.value)}>
              {operatorOptions.map((entry) => (
                <option key={entry.value} value={entry.value}>{entry.label}</option>
              ))}
            </select>
          </label>
          <label>
            <span>{firstValueLabel}</span>
            <input value={formula1} aria-label={firstValueLabel} title={firstValueLabel} onChange={(event) => setFormula1(event.target.value)} />
          </label>
          <label>
            <span>{secondValueLabel}</span>
            <input value={formula2} aria-label={secondValueLabel} title={secondValueLabel} onChange={(event) => setFormula2(event.target.value)} />
          </label>
          <label>
            <span>Applies to</span>
            <input
              value={rangeDraft}
              aria-label="Applies to ranges"
              title="Applies to ranges"
              onChange={(event) => {
                setRangeDraft(event.target.value)
                setDraftError(null)
              }}
            />
          </label>
          <div className="fg-excel-conditional-preview" style={{
            background: visualRule ? String(selectedStyle.style.backgroundColor || '#fff') : customFillColor,
            color: visualRule ? String(selectedStyle.style.textColor || '#111827') : customTextColor,
            fontWeight: !visualRule && customBold ? 700 : 400,
            fontStyle: !visualRule && customItalic ? 'italic' : 'normal',
            textDecoration: !visualRule && customUnderline ? 'underline' : 'none',
            border: !visualRule && customBorderStyle !== 'none' ? `1px solid ${customBorderColor}` : undefined,
          }}>
            {editingRule ? 'Editing rule' : 'Preview'}
          </div>
          <label className="fg-excel-format-check">
            <input
              type="checkbox"
              checked={stopIfTrue}
              aria-label="Stop if true"
              title="Stop if true"
              onChange={(event) => setStopIfTrue(event.target.checked)}
            />
            <span>Stop if true</span>
          </label>
          {visualRule ? (
            <>
              <label>
                <span>Minimum type</span>
                <select
                  value={visualMinType}
                  aria-label="Visual minimum type"
                  title="Visual minimum type"
                  onChange={(event) => setVisualMinType(event.target.value)}
                >
                  {CONDITIONAL_FORMAT_VISUAL_THRESHOLD_TYPES.filter((entry) => entry.value !== 'highestValue').map((entry) => (
                    <option key={entry.value} value={entry.value}>{entry.label}</option>
                  ))}
                </select>
              </label>
              <label>
                <span>Maximum type</span>
                <select
                  value={visualMaxType}
                  aria-label="Visual maximum type"
                  title="Visual maximum type"
                  onChange={(event) => setVisualMaxType(event.target.value)}
                >
                  {CONDITIONAL_FORMAT_VISUAL_THRESHOLD_TYPES.filter((entry) => entry.value !== 'lowestValue').map((entry) => (
                    <option key={entry.value} value={entry.value}>{entry.label}</option>
                  ))}
                </select>
              </label>
            </>
          ) : null}
          {!visualRule ? (
            <>
              <label>
                <span>Fill</span>
                <input
                  type="color"
                  value={customFillColor}
                  aria-label="Conditional fill color"
                  title="Conditional fill color"
                  onInput={(event) => setCustomFillColor(event.currentTarget.value)}
                  onChange={(event) => setCustomFillColor(event.target.value)}
                />
              </label>
              <label>
                <span>Font</span>
                <input
                  type="color"
                  value={customTextColor}
                  aria-label="Conditional font color"
                  title="Conditional font color"
                  onInput={(event) => setCustomTextColor(event.currentTarget.value)}
                  onChange={(event) => setCustomTextColor(event.target.value)}
                />
              </label>
              <label className="fg-excel-format-check">
                <input
                  type="checkbox"
                  checked={customBold}
                  aria-label="Conditional bold"
                  title="Conditional bold"
                  onChange={(event) => setCustomBold(event.target.checked)}
                />
                <span>Bold</span>
              </label>
              <label className="fg-excel-format-check">
                <input
                  type="checkbox"
                  checked={customItalic}
                  aria-label="Conditional italic"
                  title="Conditional italic"
                  onChange={(event) => setCustomItalic(event.target.checked)}
                />
                <span>Italic</span>
              </label>
              <label className="fg-excel-format-check">
                <input
                  type="checkbox"
                  checked={customUnderline}
                  aria-label="Conditional underline"
                  title="Conditional underline"
                  onChange={(event) => setCustomUnderline(event.target.checked)}
                />
                <span>Underline</span>
              </label>
              <label>
                <span>Border</span>
                <select value={customBorderStyle} aria-label="Conditional border style" title="Conditional border style" onChange={(event) => setCustomBorderStyle(event.target.value)}>
                  {CONDITIONAL_FORMAT_BORDER_STYLE_OPTIONS.map((entry) => (
                    <option key={entry.value} value={entry.value}>{entry.label}</option>
                  ))}
                </select>
              </label>
              <label>
                <span>Border color</span>
                <input
                  type="color"
                  value={customBorderColor}
                  aria-label="Conditional border color"
                  title="Conditional border color"
                  onInput={(event) => setCustomBorderColor(event.currentTarget.value)}
                  onChange={(event) => setCustomBorderColor(event.target.value)}
                />
              </label>
            </>
          ) : null}
          {kind === 'dataBar' ? (
            <>
              <label>
                <span>Bar color</span>
                <input type="color" value={dataBarColor} aria-label="Data bar color" title="Data bar color" onInput={(event) => setDataBarColor(event.currentTarget.value)} onChange={(event) => setDataBarColor(event.target.value)} />
              </label>
              <label>
                <span>Negative bar</span>
                <input type="color" value={dataBarNegativeColor} aria-label="Negative data bar color" title="Negative data bar color" onInput={(event) => setDataBarNegativeColor(event.currentTarget.value)} onChange={(event) => setDataBarNegativeColor(event.target.value)} />
              </label>
              <label>
                <span>Border</span>
                <input type="color" value={dataBarBorderColor} aria-label="Data bar border color" title="Data bar border color" onInput={(event) => setDataBarBorderColor(event.currentTarget.value)} onChange={(event) => setDataBarBorderColor(event.target.value)} />
              </label>
              <label>
                <span>Direction</span>
                <select value={dataBarDirection} aria-label="Data bar direction" title="Data bar direction" onChange={(event) => setDataBarDirection(event.target.value)}>
                  {CONDITIONAL_FORMAT_DATA_BAR_DIRECTION_OPTIONS.map((entry) => (
                    <option key={entry.value} value={entry.value}>{entry.label}</option>
                  ))}
                </select>
              </label>
              <label className="fg-excel-format-check">
                <input
                  type="checkbox"
                  checked={dataBarShowValue}
                  aria-label="Show data bar value"
                  title="Show data bar value"
                  onChange={(event) => setDataBarShowValue(event.target.checked)}
                />
                <span>Show value</span>
              </label>
            </>
          ) : null}
          {kind === 'colorScale' ? (
            <>
              <label>
                <span>Minimum</span>
                <input type="color" value={scaleMinColor} aria-label="Color scale minimum color" title="Color scale minimum color" onInput={(event) => setScaleMinColor(event.currentTarget.value)} onChange={(event) => setScaleMinColor(event.target.value)} />
              </label>
              <label>
                <span>Midpoint</span>
                <input type="color" value={scaleMidColor} aria-label="Color scale midpoint color" title="Color scale midpoint color" onInput={(event) => setScaleMidColor(event.currentTarget.value)} onChange={(event) => setScaleMidColor(event.target.value)} />
              </label>
              <label>
                <span>Midpoint type</span>
                <select value={visualMidType} aria-label="Color scale midpoint type" title="Color scale midpoint type" onChange={(event) => setVisualMidType(event.target.value)}>
                  {CONDITIONAL_FORMAT_VISUAL_THRESHOLD_TYPES.map((entry) => (
                    <option key={entry.value} value={entry.value}>{entry.label}</option>
                  ))}
                </select>
              </label>
              <label>
                <span>Midpoint value</span>
                <input
                  value={visualMidValue}
                  aria-label="Color scale midpoint value"
                  title="Color scale midpoint value"
                  onChange={(event) => setVisualMidValue(event.target.value)}
                />
              </label>
              <label>
                <span>Maximum</span>
                <input type="color" value={scaleMaxColor} aria-label="Color scale maximum color" title="Color scale maximum color" onInput={(event) => setScaleMaxColor(event.currentTarget.value)} onChange={(event) => setScaleMaxColor(event.target.value)} />
              </label>
            </>
          ) : null}
          {kind === 'iconSet' ? (
            <>
              <label>
                <span>Icon set</span>
                <select value={iconSet} aria-label="Icon set" title="Icon set" onChange={(event) => setIconSet(event.target.value)}>
                  {CONDITIONAL_FORMAT_ICON_SET_OPTIONS.map((entry) => (
                    <option key={entry.value} value={entry.value}>{entry.label}</option>
                  ))}
                </select>
              </label>
              <label className="fg-excel-format-check">
                <input
                  type="checkbox"
                  checked={iconSetShowValue}
                  aria-label="Show icon set value"
                  title="Show icon set value"
                  onChange={(event) => setIconSetShowValue(event.target.checked)}
                />
                <span>Show value</span>
              </label>
              <label className="fg-excel-format-check">
                <input
                  type="checkbox"
                  checked={iconSetReverse}
                  aria-label="Reverse icon set order"
                  title="Reverse icon set order"
                  onChange={(event) => setIconSetReverse(event.target.checked)}
                />
                <span>Reverse order</span>
              </label>
              <label>
                <span>Threshold type</span>
                <select value={iconSetThresholdType} aria-label="Icon threshold type" title="Icon threshold type" onChange={(event) => setIconSetThresholdType(event.target.value)}>
                  {CONDITIONAL_FORMAT_ICON_THRESHOLD_TYPE_OPTIONS.map((entry) => (
                    <option key={entry.value} value={entry.value}>{entry.label}</option>
                  ))}
                </select>
              </label>
              <label>
                <span>Thresholds</span>
                <input
                  value={iconSetThresholds}
                  aria-label="Icon thresholds"
                  title="Icon thresholds"
                  placeholder="33, 67"
                  onChange={(event) => setIconSetThresholds(event.target.value)}
                />
              </label>
            </>
          ) : null}
        </div>
        {draftError ? <div className="fg-excel-conditional-error">{draftError}</div> : null}
        <div className="fg-excel-conditional-manager">
          <div className="fg-excel-conditional-manager-header">
            <strong>Manage Rules</strong>
            <label className="fg-excel-conditional-filter">
              <span>Show rules for</span>
              <select value={ruleFilter} aria-label="Show formatting rules for" title="Show formatting rules for" onChange={(event) => setRuleFilter(event.target.value as ConditionalFormatRuleFilter)}>
                {CONDITIONAL_FORMAT_RULE_FILTER_OPTIONS.map((entry) => (
                  <option key={entry.value} value={entry.value}>{entry.label}</option>
                ))}
              </select>
            </label>
            <button type="button" title="Refresh rules" onClick={() => void onRefresh?.()}>Refresh</button>
          </div>
          {error ? <div className="fg-excel-conditional-error">{error}</div> : null}
          {loading ? (
            <div className="fg-excel-conditional-empty">Loading rules...</div>
          ) : visibleRules.length === 0 ? (
            <div className="fg-excel-conditional-empty">No conditional formatting rules.</div>
          ) : (
            <div className="fg-excel-conditional-rules" role="table" aria-label="Conditional formatting rules">
              {visibleRules.map((rule, index) => (
                <div className="fg-excel-conditional-rule" role="row" key={rule.id}>
                  <span title={rule.id}>{rule.kind || 'Rule'}</span>
                  <span>{rule.ranges.map(formatConditionalFormatRuleRange).join(', ')}</span>
                  <span>{rule.stop_if_true ? 'Stop' : ''}</span>
                  <button type="button" title="Move rule up" disabled={index === 0} onClick={() => moveRule(rule.id, -1)}>Up</button>
                  <button type="button" title="Move rule down" disabled={index === visibleRules.length - 1} onClick={() => moveRule(rule.id, 1)}>Down</button>
                  <button type="button" title="Edit rule" onClick={() => beginEditRule(rule)}>Edit</button>
                  <button type="button" title="Delete rule" onClick={() => {
                    void Promise.resolve(onDeleteRule?.(rule.id)).then(() => {
                      onApplied?.()
                      void onRefresh?.()
                    })
                  }}>Delete</button>
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="fg-excel-format-footer">
          {editingRule ? <button type="button" title="Cancel rule edit" onClick={resetDraft}>Cancel Edit</button> : null}
          <button type="button" title="Clear rules from selection" onClick={clearSelectionRules}>Clear Rules</button>
          <button type="button" title="Cancel" onClick={onClose}>Cancel</button>
          <button type="button" title="Apply conditional formatting rule" className="fg-excel-cell-metadata-button-primary" onClick={applyRule}>{editingRule ? 'Save' : 'OK'}</button>
        </div>
      </div>
    </div>
  )
}
